import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../utils/api';
import { Mail, ArrowLeft, ArrowRight, CheckCircle2, KeyRound } from 'lucide-react';

const TEAL = '#4DB6AC';

/**
 * Universal forgot-password page. `scope` decides which backend endpoint to call
 * and which login screen the "Back" link points to.
 *   - scope="org"        → POST /api/auth/forgot-password,        back to /login
 *   - scope="community"  → POST /api/community/auth/forgot-password, back to /community/login
 */
export default function ForgotPasswordPage({ scope = 'org' }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const isCommunity = scope === 'community';
  const endpoint = isCommunity ? '/community/auth/forgot-password' : '/auth/forgot-password';
  const backTo = isCommunity ? '/community/login' : '/login';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}${endpoint}`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8FAF9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 440, width: '100%', background: '#fff', borderRadius: 16, padding: 32, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: `${TEAL}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <CheckCircle2 size={32} color={TEAL} />
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1F2937', marginBottom: 8 }}>Check your inbox</h1>
          <p style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.6, marginBottom: 20 }}>
            We've sent a password reset link to <strong>{email}</strong>. The link will expire in 24 hours.
          </p>
          <button type="button" data-testid="back-to-login-btn" onClick={() => navigate(backTo)}
            style={{ width: '100%', padding: '12px', background: TEAL, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAF9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 420, width: '100%' }}>
        <button type="button" onClick={() => navigate(backTo)}
          style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 6, color: '#6B7280', fontSize: 13, cursor: 'pointer', marginBottom: 16, padding: 0 }}>
          <ArrowLeft size={14} /> Back to sign in
        </button>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: `linear-gradient(135deg, ${TEAL}, #2E8B7A)`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <KeyRound size={26} color="#fff" />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1F2937', marginBottom: 4 }}>Reset your password</h1>
          <p style={{ fontSize: 14, color: '#6B7280' }}>
            Enter the email linked to your account and we'll send you a reset link.
          </p>
        </div>

        <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          {error && (
            <div data-testid="forgot-password-error" style={{ padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, fontSize: 13, color: '#B91C1C', marginBottom: 16 }}>{error}</div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ position: 'relative' }}>
              <Mail size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
              <input data-testid="forgot-password-email" type="email" required placeholder="Email"
                value={email} onChange={e => setEmail(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '12px 12px 12px 40px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: 15, outline: 'none' }}
                onFocus={e => e.target.style.borderColor = TEAL}
                onBlur={e => e.target.style.borderColor = '#E5E7EB'} />
            </div>
            <button type="submit" data-testid="forgot-password-submit" disabled={loading}
              style={{ width: '100%', padding: '14px', background: loading ? '#9CA3AF' : TEAL, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 4px 12px rgba(77,182,172,0.25)' }}>
              {loading ? 'Sending…' : (<>Send reset link <ArrowRight size={18} /></>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
